import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';

import {
  AddLink as AddLinkIcon,
  AutoAwesome as AiIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  VisibilityOff as HideIcon,
  InfoOutlined as InfoOutlinedIcon,
  Refresh as RefreshIcon,
  SearchOffRounded as SearchOffRoundedIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  ACCEPTED_IMAGE_DROPZONE_TYPES,
  MAX_IMAGE_SIZE,
  attractionQueryKeys,
  useAddAttractionOffer,
  useExcludeAttractionOffer,
  useGetAttraction,
  useGetAttractionImages,
  usePutAttractionOffersOrder,
  useResetAttractionImage,
  useRestoreAttractionOffer,
  useUpdateAttractionImage,
  useUpdateAttractionName,
  useUploadAttractionImage,
} from '~/hooks/apis/attractions';
import useAppNavigation from '~/hooks/useAppNavigation';

import { getAttractionOffers, putAttractionOffersOrder } from '~/services/AttractionsService';

import { type MatchedOffer, VisibilityFilter, formatAttractionType, isCustomerVisible } from '~/types/attractionTypes';

import { ReorderableOffersTableBody, StaticLinkedOfferRow } from './LinkedOfferTableRows';
import ProximityMap from './ProximityMap';

const DEFAULT_PAGE_SIZE = 10;
const REORDER_MAX_ACTIVE = 100;

type ReorderCatalogPayload = { offers: Array<MatchedOffer>; total: number };

const OFFER_COLUMNS = ['Title', 'Provider', 'Match Type', 'Visibility', 'Relevance', 'Actions'] as const;

export default function AttractionDetailPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const { push } = useAppNavigation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { attractionId } = useParams<{ attractionId: string }>();

  const [offersPage, setOffersPage] = useState(1);
  const [offersPageSize, setOffersPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showExcluded, setShowExcluded] = useState(false);
  const [offersSearchText, setOffersSearchText] = useState('');
  const [offersSearchDebounced, setOffersSearchDebounced] = useState('');
  const offersSearchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [isAddOfferOpen, setIsAddOfferOpen] = useState(false);
  const [addOfferInput, setAddOfferInput] = useState('');

  const [matchTypeFilter, setMatchTypeFilter] = useState<string>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all');
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  /** Local order while reordering; reset when entering or exiting reorder mode */
  const [reorderDraft, setReorderDraft] = useState<Array<MatchedOffer> | null>(null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  const { data: attraction, isLoading: isAttractionLoading } = useGetAttraction(attractionId);

  const excludeMutation = useExcludeAttractionOffer();
  const restoreMutation = useRestoreAttractionOffer();
  const addOfferMutation = useAddAttractionOffer();
  const putOffersOrderMutation = usePutAttractionOffersOrder();

  // Promote: adds a candidate offer to the customer-visible set by appending it
  // to the current ordered list (sets displaySequence).
  const promoteMutation = useMutation({
    mutationFn: async ({ attractionId: aid, offerId }: { attractionId: string; offerId: string }) => {
      // Fetch current customer-visible offers to get the order
      const current = await getAttractionOffers(aid, { limit: 200, offset: 0 });
      const visibleIds = (current.result ?? [])
        .filter((o: MatchedOffer) => isCustomerVisible(o))
        .map((o: MatchedOffer) => o.id);
      // Append the promoted offer at the end (deduplicate in case of race)
      const newOrder = [...visibleIds.filter((id) => id !== offerId), offerId];
      await putAttractionOffersOrder(aid, newOrder);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.offers(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.detail(attractionId) });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      enqueueSnackbar('Offer promoted to customer display', { variant: 'success' });
    },
    onError: (err) => {
      enqueueSnackbar(`Failed to promote offer: ${err instanceof Error ? err.message : String(err)}`, {
        variant: 'error',
      });
    },
  });
  const updateNameMutation = useUpdateAttractionName();

  const handleStartEditName = useCallback(() => {
    setNameInput(attraction?.name ?? '');
    setIsEditingName(true);
  }, [attraction?.name]);

  const handleSaveName = () => {
    if (updateNameMutation.isPending) {
      return;
    }
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === attraction?.name) {
      setIsEditingName(false);
      return;
    }
    updateNameMutation.mutate({ attractionId, name: trimmed }, { onSuccess: () => setIsEditingName(false) });
  };

  const handleOffersSearchChange = useCallback((value: string) => {
    setOffersSearchText(value);
    clearTimeout(offersSearchTimer.current);
    offersSearchTimer.current = setTimeout(() => {
      setOffersSearchDebounced(value);
      setOffersPage(1);
    }, 400);
  }, []);

  useEffect(() => {
    return () => clearTimeout(offersSearchTimer.current);
  }, []);

  // Fetch a bounded full table slice so filters and counts are table-wide, not page-scoped.
  const {
    data: fullSetData,
    isLoading: isOffersLoading,
    isFetching: isOffersFetching,
    error: offersError,
  } = useQuery({
    queryKey: [...attractionQueryKeys.offers(attractionId), showExcluded ? 'all-excluded' : 'all-active'] as const,
    queryFn: async ({ signal }) => {
      const all = await getAttractionOffers(
        attractionId,
        { limit: 200, offset: 0, excludedOnly: showExcluded || undefined },
        { signal },
      );
      return all.result ?? [];
    },
    enabled: !!attractionId,
    staleTime: 30_000,
  });

  const allOffers = useMemo(
    () => (showExcluded ? (fullSetData ?? []).filter((o) => o.excluded) : (fullSetData ?? [])),
    [fullSetData, showExcluded],
  );
  const visibleCount = allOffers.filter((o) => isCustomerVisible(o)).length;
  const needsReviewCount = allOffers.filter((o) => !isCustomerVisible(o)).length;

  const { effectiveOffers, filteredTotalCount } = useMemo(() => {
    const searchLower = offersSearchDebounced.trim().toLowerCase();
    const filtered = allOffers
      .filter((o) => matchTypeFilter === 'all' || o.matchType === matchTypeFilter)
      .filter((o) => {
        // Excluded view ignores the customer-display filter (it is not shown there).
        if (showExcluded || visibilityFilter === 'all') {
          return true;
        }
        if (visibilityFilter === 'customerVisible') {
          return isCustomerVisible(o);
        }
        return !isCustomerVisible(o);
      })
      .filter((o) => !searchLower || o.title.toLowerCase().includes(searchLower) || o.id.toLowerCase() === searchLower);
    // "Visible + Needs review": visible first, then by relevance desc within each group.
    // Individual filters: relevance desc only (backend default order).
    if (!showExcluded && visibilityFilter === 'all') {
      filtered.sort((a, b) => {
        const aVisible = isCustomerVisible(a) ? 1 : 0;
        const bVisible = isCustomerVisible(b) ? 1 : 0;
        if (aVisible !== bVisible) {
          return bVisible - aVisible;
        }
        return (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0);
      });
    }
    return {
      effectiveOffers: filtered.slice((offersPage - 1) * offersPageSize, offersPage * offersPageSize),
      filteredTotalCount: filtered.length,
    };
  }, [showExcluded, visibilityFilter, matchTypeFilter, allOffers, offersPage, offersPageSize, offersSearchDebounced]);

  const offersTotal = filteredTotalCount;

  const reorderEligible =
    !showExcluded &&
    !offersSearchDebounced.trim() &&
    matchTypeFilter === 'all' &&
    (visibilityFilter === 'all' || visibilityFilter === 'customerVisible');

  const {
    data: reorderCatalogData,
    isPending: isReorderCatalogPending,
    isFetching: isReorderCatalogFetching,
    isError: isReorderCatalogError,
    error: reorderCatalogError,
  } = useQuery({
    queryKey: ['attraction-offers-reorder-load', attractionId] as const,
    enabled: reorderMode && reorderEligible && !!attractionId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<ReorderCatalogPayload> => {
      const res = await getAttractionOffers(attractionId, { limit: REORDER_MAX_ACTIVE, offset: 0 }, { signal });
      return { offers: res.result ?? [], total: res.total ?? 0 };
    },
  });

  const reorderCatalogTotal = reorderCatalogData?.total ?? 0;

  useEffect(() => {
    setReorderDraft(null);
  }, [attractionId]);

  useEffect(() => {
    if (!reorderMode) {
      setReorderDraft(null);
      return;
    }
    const server = reorderCatalogData?.offers;
    if (!server?.length) {
      return;
    }
    // Reorder only operates on customer-visible offers to prevent accidentally
    // setting displaySequence on every proximity candidate.
    const customerVisible = server.filter((o) => isCustomerVisible(o));
    setReorderDraft((prev) => {
      if (prev === null || prev.length === 0) {
        return customerVisible;
      }
      if (prev.length !== customerVisible.length || !customerVisible.every((o) => prev.some((p) => p.id === o.id))) {
        return customerVisible;
      }
      return prev;
    });
  }, [reorderMode, reorderCatalogData]);

  const sortableOffers = reorderDraft ?? [];

  const offersFooterCount = reorderMode
    ? sortableOffers.length > 0
      ? sortableOffers.length
      : reorderCatalogTotal > 0
        ? reorderCatalogTotal
        : offersTotal
    : offersTotal;

  const isReorderInitializing =
    reorderMode &&
    reorderEligible &&
    !!attractionId &&
    (isReorderCatalogPending || (isReorderCatalogFetching && !reorderCatalogData));

  const {
    data: imagesData,
    isLoading: isImagesLoading,
    error: imagesError,
  } = useGetAttractionImages(attractionId, {
    enabled: isImagePickerOpen,
  });
  const images = imagesData?.result ?? [];

  const setImageMutation = useUpdateAttractionImage();
  const resetImageMutation = useResetAttractionImage();
  const uploadImageMutation = useUploadAttractionImage();

  const onDropImage = useCallback(
    (acceptedFiles: Array<File>, fileRejections: Array<FileRejection>) => {
      fileRejections.forEach(({ file, errors }) => {
        enqueueSnackbar(`"${file.name}" ${errors.map((e) => e.message).join(' and ')}`, { variant: 'error' });
      });
      const file = acceptedFiles[0];
      if (file) {
        uploadImageMutation.mutate({ attractionId, file });
      }
    },
    [attractionId, uploadImageMutation, enqueueSnackbar],
  );

  const {
    getRootProps: getUploadRootProps,
    getInputProps: getUploadInputProps,
    isDragActive: isUploadDragActive,
  } = useDropzone({
    onDrop: onDropImage,
    accept: ACCEPTED_IMAGE_DROPZONE_TYPES,
    maxFiles: 1,
    maxSize: MAX_IMAGE_SIZE,
    disabled: uploadImageMutation.isPending,
  });

  const offersPageCount = Math.ceil(offersTotal / offersPageSize);

  const handleOffersPageChange = useCallback((_event: React.ChangeEvent<unknown>, value: number) => {
    setOffersPage(value);
  }, []);

  const handleOffersPageSizeChange = useCallback((event: SelectChangeEvent<number>) => {
    setOffersPageSize(Number(event.target.value));
    setOffersPage(1);
  }, []);

  const handleSetImage = useCallback(
    (imageId: string, imageUrl: string) => {
      setImageMutation.mutate({ attractionId, imageId, imageUrl }, { onSuccess: () => setIsImagePickerOpen(false) });
    },
    [attractionId, setImageMutation],
  );

  const handleResetImage = useCallback(() => {
    resetImageMutation.mutate(attractionId, {
      onSuccess: () => setIsImagePickerOpen(false),
    });
  }, [attractionId, resetImageMutation]);

  const handleAddOffer = useCallback(() => {
    const offerId = addOfferInput.trim();
    if (!offerId || addOfferMutation.isPending) {
      return;
    }
    addOfferMutation.mutate(
      { attractionId, offerId },
      {
        onSuccess: () => {
          setIsAddOfferOpen(false);
          setAddOfferInput('');
          setOffersPage(1);
        },
      },
    );
  }, [attractionId, addOfferInput, addOfferMutation]);

  const exitReorderMode = useCallback(() => {
    setReorderMode(false);
    setReorderDraft(null);
    queryClient.removeQueries({ queryKey: ['attraction-offers-reorder-load', attractionId] });
  }, [attractionId, queryClient]);

  const handleSaveOffersOrder = useCallback(() => {
    if (!reorderDraft?.length) {
      return;
    }
    putOffersOrderMutation.mutate(
      { attractionId, offerIdsInOrder: reorderDraft.map((o) => o.id) },
      {
        onSuccess: () => {
          exitReorderMode();
        },
      },
    );
  }, [attractionId, exitReorderMode, putOffersOrderMutation, reorderDraft]);

  if (isAttractionLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!attraction) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Attraction Not Found" backButton />
        <Alert severity="error">Attraction not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <PageHeader
        title={
          isEditingName ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveName();
                  }
                  if (e.key === 'Escape') {
                    setIsEditingName(false);
                  }
                }}
                size="small"
                autoFocus
                inputProps={{ maxLength: 200 }}
                sx={{ minWidth: 320 }}
              />
              <IconButton size="small" color="primary" onClick={handleSaveName} disabled={updateNameMutation.isPending}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setIsEditingName(false)} disabled={updateNameMutation.isPending}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                {attraction.name}
              </Typography>
              <Tooltip title="Edit name">
                <IconButton size="small" onClick={handleStartEditName}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        }
        subtitle={`${attraction.city}, ${attraction.country} (${attraction.countryCode})`}
        backButton
        sticky
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={formatAttractionType(attraction.type)} size="small" />
          <Chip
            label={attraction.active ? 'Active' : 'Inactive'}
            size="small"
            color={attraction.active ? 'success' : 'default'}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </PageHeader>

      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {attraction.description || 'No description'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', height: 320 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRight: '1px solid',
              borderColor: 'divider',
              minWidth: 100,
            }}
          >
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                #{attraction.popularityRank}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', mt: 0.25 }}
              >
                Rank
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                {attraction.offerCount}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', mt: 0.25 }}
              >
                {/* Gated, customer-visible count; the Linked Offers list below shows the raw
                    curator catalog, which can be much larger. Distinct labels avoid the
                    "header says 0, list shows 50" confusion. */}
                Customer visible
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <ProximityMap
              latitude={attraction.latitude}
              longitude={attraction.longitude}
              name={attraction.name}
              radiusMeters={5000}
              offers={effectiveOffers}
              totalOffers={offersTotal}
              onOfferClick={(offerId) => push(`/experience/${offerId}`)}
            />
          </Box>

          <Box
            sx={{
              width: 360,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                Hero Image
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: 12 }} />}
                sx={{ fontSize: '0.7rem' }}
                onClick={() => setIsImagePickerOpen(true)}
              >
                Change
              </Button>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
              {attraction.imageUrl ? (
                <Box
                  component="img"
                  src={
                    attraction.imageUrl.startsWith('http')
                      ? attraction.imageUrl
                      : `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_400,h_300/${attraction.imageUrl}`
                  }
                  alt={attraction.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: attraction.imageUrl.includes('wikimedia') ? 'contain' : 'cover',
                    backgroundColor: attraction.imageUrl.includes('wikimedia') ? '#f5f5f5' : undefined,
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.disabled">
                    No image set
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: alpha(theme.palette.action.hover, 0.02),
          }}
        >
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            ID:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {attraction.attractionId}
            </Box>
          </Typography>
          {attraction.lePlaceId && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              Place:{' '}
              <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {attraction.lePlaceId}
              </Box>
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AiIcon sx={{ fontSize: 12, color: 'primary.main' }} />
            <Typography variant="caption" color="primary" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
              AI Generated
            </Typography>
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ position: 'relative' }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {reorderMode ? (
            <>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Ordering customer-visible offers
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Only visible offers can be ordered. Candidates must be promoted first.
                </Typography>
              </Box>
              <Button size="small" variant="text" onClick={exitReorderMode} sx={{ fontSize: '0.75rem' }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={putOffersOrderMutation.isPending || isReorderInitializing || !reorderDraft?.length}
                onClick={handleSaveOffersOrder}
                sx={{ fontSize: '0.75rem' }}
              >
                {putOffersOrderMutation.isPending ? 'Saving...' : 'Save order'}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
                {showExcluded ? 'Excluded Offers' : 'Linked Offers (raw catalog)'} ({offersTotal})
                {!showExcluded && (visibleCount > 0 || needsReviewCount > 0) && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}
                  >
                    {visibleCount} visible · {needsReviewCount} needs review
                  </Typography>
                )}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HideIcon sx={{ fontSize: 14, color: showExcluded ? 'error.main' : 'text.disabled' }} />
                <Typography
                  variant="caption"
                  color={showExcluded ? 'error' : 'text.secondary'}
                  sx={{ fontSize: '0.7rem' }}
                >
                  Show excluded
                </Typography>
                <Switch
                  size="small"
                  checked={showExcluded}
                  onChange={(e) => {
                    setShowExcluded(e.target.checked);
                    if (e.target.checked) {
                      setVisibilityFilter('all');
                    }
                    setOffersPage(1);
                  }}
                  color="error"
                />
              </Stack>
              <Tooltip
                title={
                  !reorderEligible
                    ? 'Clear search, turn off excluded view, and reset filters first'
                    : 'Order the customer-visible offers'
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!reorderEligible || offersTotal === 0}
                    onClick={() => {
                      setReorderDraft(null);
                      setReorderMode(true);
                    }}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Reorder visible offers
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddLinkIcon />}
                onClick={() => setIsAddOfferOpen(true)}
                sx={{ fontSize: '0.75rem' }}
              >
                Add Offer
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
            {reorderMode
              ? 'Drag or set the order for visible offers only. Candidates are not included until promoted.'
              : 'Visible offers appear on the customer site. Needs review offers are curator candidates and stay hidden until promoted.'}
          </Typography>
        </Box>
        {!reorderMode && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <TextField
              label="Search name"
              value={offersSearchText}
              sx={{ flex: 1 }}
              onChange={(e) => handleOffersSearchChange(e.target.value)}
              size="small"
              InputProps={{
                ...(offersSearchText && {
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => {
                        clearTimeout(offersSearchTimer.current);
                        setOffersSearchText('');
                        setOffersSearchDebounced('');
                        setOffersPage(1);
                      }}
                    >
                      <SearchOffRoundedIcon fontSize="small" />
                    </IconButton>
                  ),
                }),
              }}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                select
                size="small"
                label="Match source"
                value={matchTypeFilter}
                onChange={(e) => {
                  setMatchTypeFilter(e.target.value);
                  setOffersPage(1);
                }}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="all">All sources</MenuItem>
                <MenuItem value="PROXIMITY">Proximity candidates</MenuItem>
                <MenuItem value="MANUAL">Manual links</MenuItem>
              </TextField>
              {!showExcluded && (
                <TextField
                  select
                  size="small"
                  label="Customer display"
                  value={visibilityFilter}
                  onChange={(e) => {
                    setVisibilityFilter(e.target.value as VisibilityFilter);
                    setOffersPage(1);
                  }}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="all">Visible + Needs review</MenuItem>
                  <MenuItem value="customerVisible">Visible to customer</MenuItem>
                  <MenuItem value="candidate">Needs review</MenuItem>
                </TextField>
              )}
            </Stack>
          </Box>
        )}
        {offersError && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
            Error loading offers: {offersError instanceof Error ? offersError.message : String(offersError)}
          </Alert>
        )}
        {isOffersFetching && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, height: 2 }} />
        )}
        <TableContainer
          sx={
            reorderMode && sortableOffers.length > 48 ? { maxHeight: 'min(70vh, 560px)', overflow: 'auto' } : undefined
          }
        >
          <Table size="small" sx={{ '& .MuiTableCell-body': { fontSize: '0.8125rem', verticalAlign: 'middle' } }}>
            <TableHead>
              <TableRow>
                {reorderMode && (
                  <TableCell
                    sx={{
                      padding: 1,
                      width: 80,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'white',
                      backgroundColor: theme.palette.primary.dark,
                      textAlign: 'center',
                    }}
                  >
                    Order
                  </TableCell>
                )}
                {OFFER_COLUMNS.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      padding: 1,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'white',
                      whiteSpace: 'nowrap',
                      backgroundColor: theme.palette.primary.dark,
                      textAlign: col !== 'Title' ? 'center' : 'left',
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isOffersLoading && effectiveOffers.length === 0 && !reorderMode ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {OFFER_COLUMNS.map((col) => (
                      <TableCell key={col}>
                        <Skeleton variant="rounded" height={20} sx={{ minWidth: 40 }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : reorderMode ? (
                isReorderCatalogError ? (
                  <TableRow>
                    <TableCell colSpan={OFFER_COLUMNS.length + 1}>
                      <Alert severity="error">
                        Could not load offers for reorder (
                        {reorderCatalogError instanceof Error
                          ? reorderCatalogError.message
                          : String(reorderCatalogError)}
                        ). Try again or refresh the page.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : isReorderInitializing ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`reorder-skel-${i}`}>
                      <TableCell sx={{ width: 80 }}>
                        <Skeleton variant="rounded" height={32} width={56} />
                      </TableCell>
                      {OFFER_COLUMNS.map((col) => (
                        <TableCell key={col}>
                          <Skeleton variant="rounded" height={20} sx={{ minWidth: 40 }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : sortableOffers.length > 0 ? (
                  <ReorderableOffersTableBody
                    offers={sortableOffers}
                    attractionId={attractionId}
                    excludeMutation={excludeMutation}
                    restoreMutation={restoreMutation}
                    onReorder={setReorderDraft}
                  />
                ) : (
                  <TableRow>
                    <TableCell colSpan={OFFER_COLUMNS.length + 1} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No offers to reorder
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              ) : effectiveOffers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={OFFER_COLUMNS.length} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {showExcluded ? 'No excluded offers' : 'No offers match the current filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                effectiveOffers.map((offer) => (
                  <StaticLinkedOfferRow
                    key={offer.id}
                    offer={offer}
                    attractionId={attractionId}
                    excludeMutation={excludeMutation}
                    restoreMutation={restoreMutation}
                    promoteMutation={promoteMutation}
                    showExcludedView={showExcluded}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Pagination
            count={offersPageCount}
            page={offersPage}
            onChange={handleOffersPageChange}
            color="primary"
            size="small"
            disabled={reorderMode}
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {offersFooterCount > 0 && (
              <Typography variant="caption" color="text.disabled">
                {offersFooterCount} {offersFooterCount === 1 ? 'offer' : 'offers'}
              </Typography>
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Per page:
              </Typography>
              <Select
                size="small"
                value={offersPageSize}
                onChange={handleOffersPageSizeChange}
                disabled={reorderMode}
                sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
              >
                {[10, 25, 50].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={isAddOfferOpen}
        onClose={() => {
          setIsAddOfferOpen(false);
          setAddOfferInput('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Offer to Attraction</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the offer ID to manually link it to this attraction. The offer must be online.
          </Typography>
          <TextField
            label="Offer ID"
            value={addOfferInput}
            onChange={(e) => setAddOfferInput(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddOffer();
              }
            }}
            placeholder="e.g. rezdy-abc123"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsAddOfferOpen(false);
              setAddOfferInput('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddOffer}
            disabled={!addOfferInput.trim() || addOfferMutation.isPending}
          >
            {addOfferMutation.isPending ? 'Linking...' : 'Link Offer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isImagePickerOpen} onClose={() => setIsImagePickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Hero Image</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.info.main, 0.04),
              border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.2 }} />
            <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.5 }}>
              Images are filtered by <strong>AI Vision</strong> to show only those depicting this attraction. When no
              offer image matches, a <strong>Wikipedia Commons</strong> image is used as reference. The order reflects
              relevance to the POI. Selecting an image sets it as the hero across search results and detail pages.
            </Box>
          </Box>
          <Box
            {...getUploadRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isUploadDragActive ? 'primary.main' : 'grey.400',
              borderRadius: 1,
              p: 2,
              mb: 2,
              textAlign: 'center',
              cursor: uploadImageMutation.isPending ? 'wait' : 'pointer',
              bgcolor: isUploadDragActive ? 'action.hover' : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            <input {...getUploadInputProps()} />
            {uploadImageMutation.isPending ? (
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Uploading...</Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                <CloudUploadIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                <Typography variant="body2" color="text.secondary">
                  {isUploadDragActive ? 'Drop image here' : 'Upload custom image (JPEG, PNG, WebP)'}
                </Typography>
              </Stack>
            )}
          </Box>

          {imagesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading images: {imagesError instanceof Error ? imagesError.message : String(imagesError)}
            </Alert>
          )}
          {isImagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No images available from linked offers.
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {images.map((image) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={image.imageId}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      '&:hover': { borderColor: themeColor },
                      position: 'relative',
                    }}
                    onClick={() => handleSetImage(image.imageId, image.url)}
                  >
                    <CardMedia
                      component="img"
                      image={image.url}
                      alt={image.offerTitle}
                      sx={{
                        height: 120,
                        objectFit: image.url.includes('wikimedia') ? 'contain' : 'cover',
                        backgroundColor: image.url.includes('wikimedia') ? '#f5f5f5' : undefined,
                      }}
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {image.offerTitle}
                      </Typography>
                    </Box>
                    {setImageMutation.isPending && setImageMutation.variables?.imageId === image.imageId && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Tooltip title="Reset to auto-selected image">
            <Button startIcon={<RefreshIcon />} onClick={handleResetImage} disabled={resetImageMutation.isPending}>
              Reset to Auto
            </Button>
          </Tooltip>
          <Button onClick={() => setIsImagePickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
